import { useEffect, useMemo, useRef, useState } from "react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, FileDown, AlertTriangle, Minus, Plus, Lock, ArrowLeft } from "lucide-react";
import { InternalDocDialog } from "@/components/InternalDocDialog";
import { toast } from "sonner";
import { InventoryLockPanel, LockBadge } from "@/components/InventoryLock";

const nq = (v) => Number(v || 0).toLocaleString("sk-SK", { maximumFractionDigits: 3 });
const fmtD = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join(".") : "—");
const curMonth = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };

async function openPdf(path, params) {
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("PDF sa nepodarilo vytvoriť"); }
}

const rowCalc = (it, counts) => {
  const raw = counts ? counts[it.id] : it.counted;
  const counted = raw === "" || raw == null || Number.isNaN(Number(raw)) ? Math.max(0, Number(it.system_qty ?? it.qty ?? 0)) : Math.max(0, Number(raw));
  const pp = Number(it.purchase_price || 0);
  const g = pp ? Number(it.price_gross || 0) / pp : 1;
  const diff = Math.round((counted - Number(it.system_qty ?? it.qty ?? 0)) * 1000) / 1000;
  return { counted, diff, diffNet: diff * pp, diffGross: diff * pp * g, valNet: counted * pp, valGross: counted * pp * g };
};

function InventoryTable({ items, counts, setCount, readOnly }) {
  return (
    <div className="max-h-[50vh] overflow-auto rounded-lg border border-border">
      <table className="w-full whitespace-nowrap text-sm" data-testid="inventory-table">
        <thead className="sticky top-0 z-10 border-b border-border bg-card text-[11px] uppercase text-muted-foreground">
          <tr><th className="p-2 text-left">Názov</th><th className="p-2 text-left">Na sklade od</th><th className="p-2 text-right">Zač.</th><th className="p-2 text-right">Príjem</th><th className="p-2 text-right">Vyrad.</th><th className="p-2 text-right">Spotr.</th><th className="p-2 text-right">Evid.</th><th className="p-2 text-right">Rezerv.</th><th className="p-2 text-center">Napočítané</th><th className="p-2 text-right">Rozdiel</th><th className="p-2 text-right">Hodn. bez DPH</th><th className="p-2 text-right">s DPH</th></tr>
        </thead>
        <tbody>{items.map((it) => {
          const c = rowCalc(it, readOnly ? null : counts);
          const step = it.divisible ? 0.1 : 1;
          return (
            <tr key={it.id} className={`border-b border-border/40 ${c.diff ? "bg-amber-500/10" : ""}`} data-testid={`inventory-row-${it.id}`}>
              <td className="p-2"><div className="font-medium">{it.name}</div>{it.order_number && <div className="text-xs text-muted-foreground">{it.order_number}</div>}{it.removed && <div className="text-[11px] text-muted-foreground">vyradené zo zoznamu</div>}</td>
              <td className="p-2 text-xs">{fmtD(it.since)} <span className="text-muted-foreground">({it.months_on_stock || 0} m.)</span></td>
              <td className="p-2 text-right font-mono">{nq(it.start_qty)}</td>
              <td className="p-2 text-right font-mono text-emerald-400">{it.received_qty ? nq(it.received_qty) : "—"}</td>
              <td className="p-2 text-right font-mono text-red-400">{it.issued_qty ? nq(it.issued_qty) : "—"}</td>
              <td className="p-2 text-right font-mono text-orange-300">{it.consumed_qty ? nq(it.consumed_qty) : "—"}</td>
              <td className="p-2 text-right font-mono font-semibold" data-testid={`inventory-system-${it.id}`}>{nq(it.system_qty ?? it.qty)} {it.unit}</td>
              <td className="p-2 text-right font-mono text-amber-400">{it.reserved_qty ? nq(it.reserved_qty) : "—"}</td>
              <td className="p-2">
                {readOnly ? <div className="text-center font-mono font-semibold">{nq(c.counted)}</div> : (
                  <div className="flex items-center justify-center gap-1">
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => setCount(it.id, String(Math.max(0, Math.round((c.counted - step) * 10) / 10)))} data-testid={`inventory-dec-${it.id}`}><Minus className="h-4 w-4" /></Button>
                    <Input type="number" min="0" step={step} className="h-9 w-20 text-center text-base font-semibold" value={counts[it.id] ?? ""} onChange={(e) => setCount(it.id, e.target.value)} data-testid={`inventory-count-${it.id}`} />
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => setCount(it.id, String(Math.round((c.counted + step) * 10) / 10))} data-testid={`inventory-inc-${it.id}`}><Plus className="h-4 w-4" /></Button>
                  </div>
                )}
              </td>
              <td className={`p-2 text-right font-mono ${c.diff < 0 ? "text-red-400" : c.diff > 0 ? "text-emerald-400" : "text-muted-foreground"}`} data-testid={`inventory-diff-${it.id}`}>
                {c.diff ? <><div>{c.diff > 0 ? "+" : ""}{nq(c.diff)}</div><div className="text-[11px]">{eur(c.diffNet)} / {eur(c.diffGross)}</div></> : "—"}
              </td>
              <td className="p-2 text-right font-mono">{eur(c.valNet)}</td>
              <td className="p-2 text-right font-mono">{eur(c.valGross)}</td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function Totals({ items, counts, readOnly }) {
  const t = useMemo(() => items.reduce((a, it) => {
    const c = rowCalc(it, readOnly ? null : counts);
    a.net += c.valNet; a.gross += c.valGross;
    if (c.diff < 0) { a.mankoN += -c.diffNet; a.mankoG += -c.diffGross; a.n += 1; }
    if (c.diff > 0) { a.prebN += c.diffNet; a.prebG += c.diffGross; a.n += 1; }
    return a;
  }, { net: 0, gross: 0, mankoN: 0, mankoG: 0, prebN: 0, prebG: 0, n: 0 }), [items, counts, readOnly]);
  return (
    <div className="grid gap-2 text-sm sm:grid-cols-4" data-testid="inventory-totals">
      <div className="rounded-lg border border-primary/40 bg-card p-3">Hodnota skladu<div className="font-mono text-lg font-bold text-primary" data-testid="inventory-total-net">{eur(t.net)}</div><div className="text-xs text-muted-foreground">{eur(t.gross)} s DPH</div></div>
      <div className="rounded-lg border border-border bg-card p-3">Manko<div className="font-mono text-lg font-bold text-red-400" data-testid="inventory-total-manko">{eur(t.mankoN)}</div><div className="text-xs text-muted-foreground">{eur(t.mankoG)} s DPH</div></div>
      <div className="rounded-lg border border-border bg-card p-3">Prebytok<div className="font-mono text-lg font-bold text-emerald-400" data-testid="inventory-total-prebytok">{eur(t.prebN)}</div><div className="text-xs text-muted-foreground">{eur(t.prebG)} s DPH</div></div>
      <div className="rounded-lg border border-border bg-card p-3">Položky s rozdielom<div className="font-mono text-lg font-bold" data-testid="inventory-diff-count">{t.n}</div></div>
    </div>
  );
}

function StorePick({ stores, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-52" data-testid="inventory-store"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">Všetky sklady</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function PastList({ onOpen }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/warehouse/inventories").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list) return <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (!list.length) return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="inventory-history-empty">Zatiaľ žiadna uzavretá inventúra.</p>;
  return (
    <div className="space-y-1.5" data-testid="inventory-history-list">
      {list.map((r) => (
        <button key={r.id} type="button" onClick={() => onOpen(r.id)} className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card p-3 text-left text-sm hover:border-primary" data-testid={`inventory-history-${r.id}`}>
          <span><b className="font-mono">{r.month}</b> · {r.warehouse_name || "Sklad"} · {r.count} {r.count === 1 ? "položka" : r.count >= 2 && r.count <= 4 ? "položky" : "položiek"} · rozdiely {r.diff_count}</span>
          <span className="flex items-center gap-2"><span className="font-mono">{eur(r.totals?.counted_net)}</span><span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Uzavretá {fmtD(r.closed_at)}</span><LockBadge unlocked={r.unlocked} testid={`inventory-history-lock-${r.id}`} /></span>
        </button>
      ))}
    </div>
  );
}

export function InventoryDialog({ open, onOpenChange, stores, onChanged }) {
  const [tab, setTab] = useState("current");
  const [month, setMonth] = useState(curMonth());
  const [store, setStore] = useState("all");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [docJob, setDocJob] = useState(null);
  const [rec, setRec] = useState(null);
  const dirty = useRef(false);
  const pending = useRef(null);
  const whParam = store === "all" ? "" : store;

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/warehouse/closing", { params: { month, warehouse_id: whParam } });
      setData(d); dirty.current = false;
      setCounts(Object.fromEntries((d.items || []).map((it) => [it.id, String(it.counted)])));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (dirty.current && pending.current) { api.put("/warehouse/inventory/draft", pending.current).catch(() => {}); }
    if (open) { setTab("current"); setRec(null); load(); }
  }, [open, month, store]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty.current || !open) { return undefined; }
    pending.current = { month, warehouse_id: whParam, counts };
    const t = setTimeout(() => { api.put("/warehouse/inventory/draft", pending.current).catch(() => {}); }, 700);
    return () => clearTimeout(t);
  }, [counts]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCount = (id, v) => { dirty.current = true; setCounts((p) => ({ ...p, [id]: v })); };
  const closed = (data?.closed || []).length > 0;
  const closeInv = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(counts).filter(([, v]) => v !== "" && !Number.isNaN(Number(v))).map(([k, v]) => [k, Number(v)]));
      const { data: r } = await api.post("/warehouse/inventory/close", { month, warehouse_id: whParam, counts: payload });
      toast.success(`Inventúra uzavretá${r.removed ? ` · odstránené nulové položky: ${r.removed}` : ""}`);
      onChanged?.(); await load(); setTab("history");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openRec = async (id) => { try { const { data: r } = await api.get(`/warehouse/inventories/${id}`); setRec(r); } catch { toast.error("Inventúru sa nepodarilo načítať"); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[94vh] flex-col overflow-hidden sm:max-w-7xl" data-testid="inventory-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Inventúra skladu</DialogTitle></DialogHeader>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1 text-sm">
          <button type="button" onClick={() => { setTab("current"); setRec(null); }} className={`flex-1 rounded px-3 py-1.5 font-semibold ${tab === "current" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="inventory-tab-current">Inventúra</button>
          <button type="button" onClick={() => setTab("history")} className={`flex-1 rounded px-3 py-1.5 font-semibold ${tab === "history" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="inventory-tab-history">Predošlé inventúry</button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto">
          {tab === "current" && (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5"><Label>Sklad</Label><StorePick stores={stores} value={store} onChange={setStore} /></div>
                <div className="space-y-1.5"><Label>Mesiac</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="inventory-month" /></div>
                {closed ? <span className="rounded bg-emerald-500/15 px-2 py-1 text-xs font-bold uppercase text-emerald-300" data-testid="inventory-status-closed">{store === "all" ? `Uzavreté sklady: ${data.closed.map((c) => c.warehouse_name || "Sklad").join(", ")}` : `Uzavretá ${fmtD(data.closed[0].closed_at)}`}</span>
                  : <span className="rounded bg-amber-500/15 px-2 py-1 text-xs font-bold uppercase text-amber-300" data-testid="inventory-status-draft">Rozpracovaná{data?.draft_saved_at ? ` · uložené ${fmtD(data.draft_saved_at)}` : ""}</span>}
                <Button variant="outline" className="gap-2" onClick={() => openPdf("/warehouse/closing/pdf", { month, warehouse_id: whParam })} data-testid="inventory-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
              </div>
              {busy && !data && <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
              {data && (
                <>
                  <Totals items={data.items || []} counts={counts} />
                  {data.history_incomplete && <div className="rounded-lg border border-border bg-card/60 p-2 text-xs text-muted-foreground">História pohybov sa eviduje od 20.10.2026 – za skoršie obdobie je zobrazený iba stav bez rozpadu.</div>}
                  {(data.paid_without_doc || []).length > 0 && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="warehouse-closing-missing-docs">
                      <div className="mb-2 flex items-center gap-2 font-semibold text-amber-300"><AlertTriangle className="h-4 w-4" /> Zaplatené zákazky bez dokladu ({data.paid_without_doc.length})</div>
                      {data.paid_without_doc.map((j) => (
                        <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-500/20 py-1.5" data-testid={`warehouse-closing-missing-${j.id}`}>
                          <span><b className="font-mono">{j.number}</b> · {j.title} · {j.customer_name || "—"} · {eur(j.total)}</span>
                          <Button size="sm" variant="secondary" onClick={() => setDocJob(j)} data-testid={`warehouse-closing-issue-${j.id}`}>Vystaviť interný doklad</Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {closed ? (
                    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm" data-testid="inventory-closed-info">
                      <div className="mb-2">Inventúra za {month} je uzavretá{store === "all" ? " pre niektoré sklady – ostatné uzavriete po výbere konkrétneho skladu" : ""}. Stav po inventúre nájdete v uloženom zázname.</div>
                      <div className="flex flex-wrap gap-2">{data.closed.map((c) => <Button key={c.id} size="sm" variant="secondary" onClick={() => { setTab("history"); openRec(c.id); }} data-testid={`inventory-open-record-${c.id}`}>Zobraziť záznam {c.warehouse_name || ""}</Button>)}</div>
                    </div>
                  ) : <InventoryTable items={data.items || []} counts={counts} setCount={setCount} />}
                </>
              )}
            </>
          )}
          {tab === "history" && !rec && <PastList onOpen={openRec} />}
          {tab === "history" && rec && (
            <div className="space-y-3" data-testid="inventory-record">
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="ghost" size="sm" className="gap-1" onClick={() => setRec(null)} data-testid="inventory-record-back"><ArrowLeft className="h-4 w-4" /> Späť</Button>
                <b className="font-mono">{rec.month}</b><span>{rec.warehouse_name || "Sklad"}</span>
                <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Uzavretá {fmtD(rec.closed_at)}</span>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => openPdf(`/warehouse/inventories/${rec.id}/pdf`)} data-testid="inventory-record-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
              </div>
              <InventoryLockPanel rec={rec} onChange={(r) => { setRec(r); onChanged?.(); }} />
              <Totals items={rec.items || []} readOnly />
              <InventoryTable items={rec.items || []} readOnly />
            </div>
          )}
        </div>
        <InternalDocDialog job={docJob} open={!!docJob} onOpenChange={(v) => { if (!v) { setDocJob(null); } }} onDone={() => { load(); onChanged?.(); }} />
        <DialogFooter className="mt-2 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Zavrieť</Button>
          {tab === "current" && data && !closed && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button className="gap-2" disabled={busy || !(data.items || []).length} data-testid="inventory-close-btn"><Lock className="h-4 w-4" /> Uzavrieť inventúru</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Uzavrieť inventúru {month}?</AlertDialogTitle><AlertDialogDescription>Rozdiely sa zapíšu do skladu ako manko alebo prebytok, položky so stavom 0 bez rezervácie zmiznú zo zoznamu a uloží sa nemenný záznam. Uzavretú inventúru nie je možné upraviť.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Zrušiť</AlertDialogCancel><AlertDialogAction onClick={closeInv} data-testid="inventory-close-confirm">Uzavrieť</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConsumeDialog({ item, onClose, onDone }) {
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) { setQty("1"); setNote(""); } }, [item]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/warehouse/${item.id}/consume`, { qty: Number(qty), note }); toast.success("Vyradené ako spotreba prevádzky"); onClose(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="sm:max-w-sm" data-testid="consume-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Vyradiť ako spotrebu</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{item?.name} · na sklade {nq(item?.qty)} {item?.unit}. Odpočíta sa zo skladu bez zákazky a započíta do nákladov ako spotrebný materiál.</p>
        <div className="space-y-1.5"><Label>Množstvo</Label><Input type="number" min="0" step={item?.divisible ? "0.1" : "1"} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="consume-qty" /></div>
        <div className="space-y-1.5"><Label>Poznámka (voliteľné)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="napr. poškodený diel" data-testid="consume-note" /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Zrušiť</Button>
          <Button onClick={save} disabled={busy || !(Number(qty) > 0)} data-testid="consume-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Vyradiť</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
